import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'common-src/components/base';
import { getRoleConfig } from 'common-src/features/auth';
import { InitialFlow, PlatformStatus, RiskLevelUnset } from 'common-src/models/Patient/constants';
import {
  getInitialFlowStatus,
  getPlatformStatusColor,
  getShortenedSex,
  platformStatusPresenter,
  riskLevelColor,
  riskLevelPresenter,
} from 'common-src/models/Patient/presenters';
import { ColorsNew } from 'common-src/styles';
import { getAge } from 'common-src/utils/dateUtils';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import Icons from 'src/assets/Icons';
import { ModalType } from 'src/components/base/ModalGroup';
import { NavItem } from 'src/components/navigation';
import { openModal } from 'src/features/modals';
import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  PatientActionsButton,
  PrescriptionProviderMenu,
  ScheduleVisitMenu,
  StatusItem,
} from './items';
import styles from './PatientDetailsNavigation.module.scss';

const PatientDetailsNavigation = ({ patientId }) => {
  const dispatch = useDispatch();

  const patient = useCustomSelector((state) =>
    singleModelSelector(state, 'Patient', Number(patientId)),
  );

  const permissions = useCustomSelector(getRoleConfig)?.patientChart?.actions;

  const renderPatientStatus = () => {
    const status = getInitialFlowStatus(patient);
    return (
      <>
        <NavItem
          title={
            patient.initialFlow === InitialFlow.Guide
              ? patient.initialFlow.toUpperCase()
              : patient.initialFlow
          }
          value={<StatusItem value={status.value} backgroundColor={status.backgroundColor} />}
        />
        {patient.initialFlow !== InitialFlow.Diabetes &&
          patient.diabetesStatus !== PlatformStatus.Gray && (
            <NavItem
              title="Diabetes"
              value={
                <StatusItem
                  value={platformStatusPresenter(patient.diabetesStatus)}
                  backgroundColor={getPlatformStatusColor(patient.diabetesStatus)}
                />
              }
            />
          )}
        {patient.initialFlow !== InitialFlow.Cognitive &&
          patient.cognitiveStatus !== PlatformStatus.Gray && (
            <NavItem
              title="Cognitive"
              value={
                <StatusItem
                  value={platformStatusPresenter(patient.cognitiveStatus)}
                  backgroundColor={getPlatformStatusColor(patient.cognitiveStatus)}
                />
              }
            />
          )}
        {patient.initialFlow !== InitialFlow.Guide && patient.isGuide && (
          <NavItem title="GUIDE" value={<StatusItem value="Active" backgroundColor="#AEC082" />} />
        )}
        <div className={[styles.separator, 'm-r-20', 'primary-border'].join(' ')} />
      </>
    );
  };

  return (
    <header className={[styles.header, 'flex-row', 'vertically-centered'].join(' ')}>
      <div className="flex-row">
        <NavItem title="DOB" value={moment(patient.dob).format('MM/DD/YYYY')} />
        <NavItem title="Age" value={getAge(patient.dob)} />
        <NavItem title="Sex" value={getShortenedSex(patient.sex)} withSeparator />
        <NavItem
          title="Enrolled Date"
          value={moment(patient.createdAt).format('MM/YYYY')}
          withSeparator
        />
        <NavItem
          title="Location"
          value={patient.address?.state}
          timeZone={patient.timeZone}
          withSeparator
        />
        <NavItem title="Language" value={patient.preferredLanguage} withSeparator />
        {renderPatientStatus()}
        <NavItem
          title="Risk Level"
          value={
            <StatusItem
              value={riskLevelPresenter(patient?.riskLevel || RiskLevelUnset)}
              backgroundColor={riskLevelColor(patient?.riskLevel || RiskLevelUnset)}
            />
          }
        />
        <NavItem
          title="Lead Source"
          value={
            <StatusItem value={patient.leadSource || '-'} backgroundColor={ColorsNew.mediumGreen} />
          }
        />
      </div>
      <div className={['flex-row', 'vertically-centered', 'gap-50'].join(' ')}>
        <div className={['flex-row', 'gap-6'].join(' ')}>
          {permissions?.canAddPrescription && (
            <PrescriptionProviderMenu patient={patient} tooltipText="Prescribe Rx" />
          )}
          {permissions?.canAddMedication && (
            <Button
              id="new-medication-button"
              classNames={[styles.actionButton]}
              iconSrc={Icons.pillIcon}
              onClick={() => dispatch(openModal(ModalType.MEDICATION, { patientId: patient.id }))}
              backgroundColor="transparent"
              tooltipText="Add medication"
            />
          )}
          {permissions?.canAddLabResult && (
            <Button
              id="new-vitals-button"
              classNames={[styles.actionButton]}
              iconSrc={Icons.vitalsTabIcon}
              onClick={() => dispatch(openModal(ModalType.VITALS, { patientId: patient.id }))}
              backgroundColor="transparent"
              tooltipText="Add vitals"
            />
          )}
          <ScheduleVisitMenu patient={patient} tooltipText="Schedule" />
          {permissions?.canAddSmartGoal && (
            <Button
              id="new-smart-goal-button"
              classNames={[styles.actionButton]}
              iconSrc={Icons.newSmartIcon}
              onClick={() => dispatch(openModal(ModalType.SMART_GOAL, { patientId: patient.id }))}
              backgroundColor="transparent"
              tooltipText="Add goal"
            />
          )}
          {permissions?.canAddLabResult && (
            <Button
              id="new-lab-result-button"
              classNames={[styles.actionButton]}
              iconSrc={Icons.newLabsIcon}
              onClick={() =>
                dispatch(
                  openModal(ModalType.LAB, {
                    patientId: patient.id,
                  }),
                )
              }
              backgroundColor="transparent"
              tooltipText="Add lab result"
            />
          )}
        </div>
        <PatientActionsButton patient={patient} />
      </div>
    </header>
  );
};

PatientDetailsNavigation.propTypes = {
  patientId: PropTypes.number,
};

export default PatientDetailsNavigation;
